// Scale Emission — E = D × C² as D→0
// DSO: mass = localized drag, c = scale fabric
// Imported by materials-tabs.js
// Static JSON:   { material, tc_k, ect_ev, t_operating_k, band_gap_ev, lambda_l0_nm }
// Feedback JSON: { material, tc_k, ect_ev, p_input_w, eta }

import { GeoNum, geo } from './geo-num.js';
import { ZONES_THEORY, ZONES_MATERIALS } from './geo-num-zones.js';
import { registerReport, parseRefModel, generateReport } from './report-gen.js';

const animHandles = {};
const seCharts   = {};

const KB   = 1.381e-23;
const HBAR = 1.0546e-34;
const EV_J = 1.602e-19;

function withTheoryZones(fn) {
  GeoNum.setZoneConfig(ZONES_THEORY.boundaries, ZONES_THEORY.minZone);
  try { return fn(); }
  finally { GeoNum.setZoneConfig(ZONES_MATERIALS.boundaries, ZONES_MATERIALS.minZone); }
}

function status(msg) {
  const el = document.getElementById('s-scale-emission');
  if (el) el.textContent = msg;
}

function renderTable(headers, rows) {
  let h = '<thead><tr>' + headers.map(x => '<th>' + x + '</th>').join('') + '</tr></thead><tbody>';
  for (const r of rows) h += '<tr>' + r.map(v => '<td>' + v + '</td>').join('') + '</tr>';
  const el = document.getElementById('t-scale-emission');
  if (el) el.innerHTML = h + '</tbody>';
}

function geoDerivPanel(title, rows, geoMeta) {
  const metaHtml = geoMeta ? `
    <div class="deriv-row" style="margin-top:8px;border-top:1px solid var(--border);padding-top:6px">
      <span style="color:#4fc3f7">GeoNum Zone</span><span>${geoMeta.zone} | Shade ${geoMeta.shade}</span>
    </div>
    <div class="deriv-row"><span style="color:#4fc3f7">Drift</span><span>${geoMeta.drift.toFixed(4)} shades ${geoMeta.drift < 1.0 ? '&#x2713; stable' : '&#x26a0; drifting'}</span></div>
    <div class="deriv-row"><span style="color:#4fc3f7">Uncertainty</span><span>&plusmn;${geoMeta.unc.toExponential(3)} (${(geoMeta.relUnc * 100).toExponential(2)}%)</span></div>
    <div class="deriv-note">GeoNum precision tracking active &mdash; drift &lt; 1.0 shade = HPC-grade stable</div>` : '';
  const rowsHtml = rows.map(([k, v, cls]) =>
    `<div class="deriv-row${cls ? ' ' + cls : ''}"><span>${k}</span><span>${v}</span></div>`).join('');
  return `<div class="deriv-block"><div class="deriv-title">${title}</div>${rowsHtml}${metaHtml}</div>`;
}

// ============================================================
// Mode defaults + selector
// ============================================================
export const SE_DEFAULTS = {
  static:   '{ "material": "YBCO", "tc_k": 93, "ect_ev": 2.00, "t_operating_k": 77, "band_gap_ev": 2.00, "lambda_l0_nm": 150 }',
  feedback: '{ "material": "YBCO", "tc_k": 93, "ect_ev": 2.00, "p_input_w": 1e-8, "eta": 0.05, "lambda_l0_nm": 150 }',
};

export function seSetMode(mode) {
  const ta = document.getElementById('in-scale-emission');
  if (ta) ta.value = SE_DEFAULTS[mode] ?? SE_DEFAULTS.static;
}

// ============================================================
// Entry point
// ============================================================
export function scaleEmissionScan() {
  status('Computing...');
  let input;
  try {
    input = JSON.parse(document.getElementById('in-scale-emission').value);
  } catch (e) {
    status('JSON parse error: ' + e.message);
    return;
  }
  if (input.mode === 'feedback' || input.p_input_w != null) {
    runFeedbackScan(input);
  } else {
    runStaticScan(input);
  }
}

// ============================================================
// Static scan
// ============================================================
function runStaticScan(input) {
  const { material = 'Custom', tc_k, ect_ev, t_operating_k, band_gap_ev, lambda_l0_nm } = input;
  if (!tc_k || !ect_ev || !t_operating_k) {
    status('Required: tc_k, ect_ev, t_operating_k');
    return;
  }

  const C = 2.998e8;
  const derivEl = document.getElementById('se-derivation');

  const result = withTheoryZones(() => {
    const gRatio  = geo(t_operating_k).divide(geo(tc_k));
    const gD_norm = gRatio.multiply(gRatio).multiply(gRatio).multiply(gRatio);
    const gEct    = geo(ect_ev * EV_J);
    const gE_drag = gEct.multiply(gD_norm);
    const gE_scale = gEct.multiply(geo(Math.max(0, 1 - gD_norm.toFloat64())));
    const gRate   = geo(KB * t_operating_k).divide(geo(HBAR));
    const gPower  = gE_scale.multiply(gRate);
    const bgConv  = band_gap_ev != null
      ? Math.max(0, 1 - Math.abs(band_gap_ev - ect_ev) / ect_ev) : null;
    return {
      d_norm:     gD_norm.toFloat64(),
      e_drag_J:   gE_drag.toFloat64(),
      e_scale_J:  gE_scale.toFloat64(),
      e_scale_eV: gE_scale.toFloat64() / EV_J,
      power_W:    gPower.toFloat64(),
      bgConv,
      drift:  Math.max(gD_norm.drift, gE_scale.drift, gPower.drift),
      unc:    gE_scale.getUncertainty(),
      relUnc: gE_scale.getRelativeUncertainty(),
      zone:   gE_scale.zone,
      shade:  gE_scale.shade,
    };
  });

  const superfluid = Math.max(0, 1 - Math.pow(t_operating_k / tc_k, 4));
  const bgLabel = result.bgConv != null ? (result.bgConv * 100).toFixed(2) + '%' : 'N/A';
  const bgNote  = result.bgConv == null ? '' :
    result.bgConv > 0.95 ? '✓ Resonant channel' :
    result.bgConv > 0.80 ? '~ Near resonance' : '✗ Off-resonance';

  renderTable(
    ['Property', 'Value', 'Unit', 'Notes'],
    [
      ['Material',            material,                           '', `T_c=${tc_k}K  E_CT=${ect_ev}eV`],
      ['T_operating',         t_operating_k.toFixed(1),          'K', `T/T_c = ${(t_operating_k/tc_k).toFixed(4)}`],
      ['D_norm (T/T_c)⁴',    result.d_norm.toExponential(4),    '', 'Drag fraction remaining'],
      ['Superfluid fraction', superfluid.toFixed(6),              '', 'Cooper pair density at T'],
      ['E_drag(T)',           result.e_drag_J.toExponential(4),  'J', 'Energy locked in drag'],
      ['E_scale emitted',     result.e_scale_J.toExponential(4), 'J', result.e_scale_eV.toFixed(4) + ' eV'],
      ['Scale power density', result.power_W.toExponential(4),   'W', 'E_scale × k_BT/ℏ'],
      ['Band gap convergence',bgLabel,                            '', bgNote],
      ['GeoNum drift',        result.drift.toFixed(4) + ' shades','', result.drift < 1.0 ? '✓ HPC-grade stable' : '⚠ Drifting — physics boundary'],
    ]
  );

  const driftWarn = result.drift >= 1.0
    ? `<div class="deriv-row deriv-result" style="color:#ef5350">` +
      `⚠ Drift ${result.drift.toFixed(4)} ≥ 1.0 shades — geometric assumption breaks here. Publishable anomaly.</div>`
    : '';

  let derivHtml = geoDerivPanel(
    `Scale Emission — E = D × C² → D→0 (${material})`,
    [
      ['Framework',             'E = drag × scale²  (mass = localized drag, c = scale fabric)'],
      ['D₀ = E_CT / c²',       `${(ect_ev * EV_J / (C * C)).toExponential(4)} kg`],
      ['D(T) = D₀ × (T/T_c)⁴',`${result.d_norm.toExponential(4)} (normalized)`],
      ['Superfluid fraction',   superfluid.toFixed(6)],
      ['E_scale = E_CT×(1−D)', `${result.e_scale_eV.toFixed(4)} eV`, 'deriv-result'],
      ['Band gap convergence',  result.bgConv != null ? `${(result.bgConv*100).toFixed(2)}% — ${bgNote}` : 'N/A'],
      ['Scale power density',   `${result.power_W.toExponential(4)} W`, 'deriv-result'],
      ['Interpretation',        'As D→0 at T_c, E_drag re-expresses as Scale — the bloom gradient. Not lost.'],
    ],
    { drift: result.drift, unc: result.unc, relUnc: result.relUnc, zone: result.zone, shade: result.shade }
  ) + driftWarn;

  // Casimir prediction section — added when lambda_l0_nm provided
  if (lambda_l0_nm) {
    derivHtml += casimirPredictionPanel(tc_k, t_operating_k, lambda_l0_nm, material);
    drawCasimirChart('c-scale-emission', tc_k, t_operating_k, lambda_l0_nm, material);
  } else {
    if (seCharts['c-scale-emission']) {
      seCharts['c-scale-emission'].destroy();
      seCharts['c-scale-emission'] = null;
    }
  }

  if (derivEl) derivEl.innerHTML = derivHtml;
  animateBloomGradient('c-se-bloom', result, tc_k, t_operating_k, material);
  status(
    `Done — E_scale = ${result.e_scale_eV.toFixed(4)} eV | ` +
    `superfluid = ${(superfluid*100).toFixed(2)}% | drift = ${result.drift.toFixed(4)} shades`
  );

  const N = 100;
  registerReport({
    title:    `Scale Emission \u2014 ${material}`,
    module:   'Scale Emission',
    material,
    xLabel:   'T (K)', xMin: 0, xMax: tc_k,
    yLabel:   'E_scale / E_CT (superfluid fraction)',
    params: [
      ['Material',            material,                          `T_c = ${tc_k} K`],
      ['E_CT',                `${ect_ev} eV`,                   'Condensation energy channel'],
      ['T_operating',         `${t_operating_k} K`,             `T/T_c = ${(t_operating_k/tc_k).toFixed(4)}`],
      ['D_norm (T/T_c)\u2074',result.d_norm.toExponential(4),   'Drag fraction remaining'],
      ['Superfluid fraction', superfluid.toFixed(6),             'Cooper pair density at T'],
      ['E_scale',             `${result.e_scale_eV.toFixed(4)} eV`, 'Scale emission energy'],
      ['Scale power density', `${result.power_W.toExponential(4)} W`, 'E_scale \u00d7 k_BT/\u210f'],
      ...(lambda_l0_nm ? [
        ['\u03bb_L(0)',       `${lambda_l0_nm} nm`,             'London penetration depth'],
      ] : []),
    ],
    series: [{
      label: `GDBS Scale Emission (${material})`,
      color: '#2563eb',
      data:  Array.from({ length: N + 1 }, (_, i) => {
        const T = (i / N) * tc_k * 0.999;
        return [T, Math.max(0, 1 - Math.pow(T / tc_k, 4))];
      }),
    }],
    annotations: [],
    casimir: lambda_l0_nm ? buildCasimirMeta(tc_k, t_operating_k, lambda_l0_nm) : null,
    geoMeta: { drift: result.drift, zone: result.zone, shade: result.shade, unc: result.unc, relUnc: result.relUnc },
  });
}

// ============================================================
// Casimir anomaly prediction
// ============================================================
function casimirPredictionPanel(tc_k, t_op, lambda_l0_nm, material) {
  const sf       = Math.max(0, 1 - Math.pow(t_op / tc_k, 4));
  const lambda_t = sf > 1e-6
    ? (lambda_l0_nm / Math.sqrt(sf))
    : Infinity;
  // T where λ_L doubles: 1/sqrt(1-(T/Tc)^4)=2 → (T/Tc)^4=3/4
  const T_double = tc_k * Math.pow(0.75, 0.25);
  // T where λ_L → 5×: (T/Tc)^4 = 24/25
  const T_five   = tc_k * Math.pow(0.96, 0.25);

  const lambdaStr = isFinite(lambda_t)
    ? `${lambda_t.toFixed(1)} nm${lambda_t > 600 ? '  ⚠ diverging — very close to T_c' : ''}`
    : '∞ — at T_c';

  return geoDerivPanel(
    `Casimir Anomaly Prediction — ${material}`,
    [
      ['What Scale Emission is NOT', 'Not photons, not phonons. Primary signal is E-fabric density change around the locked ball.'],
      ['Observable coupling', 'Bloom gradient extending → local ε₀ shifts → Casimir force between nearby plates changes.'],
      ['λ_L(0)', `${lambda_l0_nm} nm     (common: YBCO~150, Nb~39, MgB₂~140, NbN~200)`],
      ['λ_L(T_op)', lambdaStr],
      ['T-dependence', `Both London and DSO corrections ∝ (1−(T/T_c)⁴) = ${sf.toFixed(4)}  — identical T-shape.`, 'deriv-result'],
      ['London d-scaling', 'ΔF_London ∝ d⁻²  (correction grows as d² relative to base Casimir d⁻⁴)'],
      ['DSO d-scaling', 'ΔF_DSO ∝ d⁻⁴  (E-fabric density change — same d-scaling as Casimir itself, constant fraction)'],
      ['Crossover d*', `${isFinite(lambda_t) ? lambda_t.toFixed(0) : '∞'} nm = λ_L(T_op)  —  London suppressed for d ≪ this`, 'deriv-result'],
      ['λ_L doubles at', `${T_double.toFixed(2)} K = ${(T_double/tc_k*100).toFixed(1)}% T_c  — sensitivity window begins`],
      ['λ_L → 5× at', `${T_five.toFixed(2)} K = ${(T_five/tc_k*100).toFixed(1)}% T_c  — London effectively decoupled`],
      ['The discriminant', 'Measure Casimir coefficient C at two plate separations d₁, d₂ ≪ λ_L(0). If C(d₁)/C(d₂) ≈ 1 → DSO (d-independent). If ≈ (d₁/d₂)² → London.', 'deriv-result'],
      ['GeoNum marker', 'Drift spike at T_c flags exactly where the London/DSO residual is largest. That is where to look in the data.'],
      ['Literature target', 'Bimonte et al. (2005–2019) Casimir-superconductor data. Subtract Lifshitz/London prediction. Fit residual to (1−(T/T_c)⁴). Check d-scaling of the coefficient.'],
    ],
    null
  );
}

function drawCasimirChart(canvasId, tc_k, t_op, lambda_l0_nm, material) {
  if (!window.Chart) return;
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  if (seCharts[canvasId]) { seCharts[canvasId].destroy(); seCharts[canvasId] = null; }

  const N = 80;
  const labels = [], sfData = [], lambdaData = [];
  for (let i = 0; i <= N; i++) {
    const T  = (i / N) * tc_k * 0.999;
    const sf = Math.max(0, 1 - Math.pow(T / tc_k, 4));
    labels.push(T.toFixed(1));
    sfData.push(+sf.toFixed(6));
    lambdaData.push(sf > 1e-6 ? +Math.min(8, 1 / Math.sqrt(sf)).toFixed(4) : 8);
  }

  // Inline plugin: vertical dashed line at T_op
  const tOpPlugin = {
    id: 'tOpLine',
    afterDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      if (!scales.x) return;
      const x = scales.x.getPixelForValue(t_op.toFixed(1));
      if (!x || x < chartArea.left || x > chartArea.right) return;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.strokeStyle = 'rgba(245,158,11,0.6)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = 'rgba(245,158,11,0.75)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`T_op=${t_op}K`, x + 3, chartArea.top + 12);
      ctx.restore();
    }
  };

  const ctx = canvas.getContext('2d');
  seCharts[canvasId] = new Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Superfluid fraction n\u209B/n  (DSO + London T-shape)',
          data: sfData,
          borderColor: '#4fc3f7',
          borderWidth: 2,
          pointRadius: 0,
          fill: { target: 'origin', above: 'rgba(79,195,247,0.07)' },
          yAxisID: 'y',
        },
        {
          label: '\u03bb\u2097(T) / \u03bb\u2097(0)  (penetration depth diverges at T\u1d9c)',
          data: lambdaData,
          borderColor: '#7c3aed',
          borderWidth: 2,
          pointRadius: 0,
          fill: false,
          yAxisID: 'y1',
        },
      ],
    },
    plugins: [tOpPlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: '#94a3b8', font: { family: 'monospace', size: 10 } } },
        title: {
          display: true,
          text: `Casimir Signature \u2014 ${material} | n\u209B/n (cyan) vs \u03bb\u2097(T)/\u03bb\u2097(0) (purple)`,
          color: '#94a3b8',
          font: { family: 'monospace', size: 10 },
        },
      },
      scales: {
        x: {
          title: { display: true, text: 'T (K)', color: '#64748b' },
          ticks: { color: '#64748b', maxTicksLimit: 10, font: { size: 9 } },
          grid: { color: 'rgba(255,255,255,0.04)' },
        },
        y: {
          min: 0, max: 1.05,
          title: { display: true, text: 'Superfluid fraction', color: '#4fc3f7' },
          ticks: { color: '#4fc3f7', font: { size: 9 } },
          grid: { color: 'rgba(79,195,247,0.06)' },
        },
        y1: {
          position: 'right',
          min: 0, max: 8,
          title: { display: true, text: '\u03bb\u2097(T)/\u03bb\u2097(0)  (capped at 8\u00d7)', color: '#7c3aed' },
          ticks: { color: '#7c3aed', font: { size: 9 } },
          grid: { drawOnChartArea: false },
        },
      },
    },
  });
}

// ============================================================
// Feedback scan — geometric fixed-point loop
// ============================================================
function pScaleFn(E_CT, tc_k) {
  return T => E_CT * Math.max(0, 1 - Math.pow(T / tc_k, 4)) * (KB * T / HBAR);
}

function dPscale_dT_fn(E_CT, tc_k) {
  return T => E_CT * (KB / HBAR) * (1 - 5 * Math.pow(T / tc_k, 4));
}

function bisect(f, lo, hi, tol = 1e-10, iters = 80) {
  for (let i = 0; i < iters; i++) {
    const mid = (lo + hi) / 2;
    if (f(lo) * f(mid) <= 0) hi = mid; else lo = mid;
    if (hi - lo < tol) break;
  }
  return (lo + hi) / 2;
}

function runFeedbackScan(input) {
  const { material = 'Custom', tc_k, ect_ev, p_input_w, eta = 0.1 } = input;
  if (!tc_k || !ect_ev || !p_input_w) {
    status('Feedback mode requires: tc_k, ect_ev, p_input_w (eta optional, default 0.1)');
    return;
  }

  const E_CT   = ect_ev * EV_J;
  const pScale = pScaleFn(E_CT, tc_k);
  const dPdT   = dPscale_dT_fn(E_CT, tc_k);

  const T_peak = tc_k * Math.pow(0.2, 0.25);
  const P_max  = pScale(T_peak) * eta;
  const f      = T => pScale(T) * eta - p_input_w;

  let stableFP = null, unstableFP = null;
  if (p_input_w <= P_max) {
    if (f(1e-6) * f(T_peak) <= 0)         stableFP   = bisect(f, 1e-6, T_peak);
    if (f(T_peak) * f(tc_k * 0.9999) <= 0) unstableFP = bisect(f, T_peak, tc_k * 0.9999);
  }

  const T_ref = stableFP ?? tc_k * 0.5;
  const geoResult = withTheoryZones(() => {
    const gRatio = geo(T_ref).divide(geo(tc_k));
    const gD     = gRatio.multiply(gRatio).multiply(gRatio).multiply(gRatio);
    const gEscale = geo(E_CT).multiply(geo(Math.max(0, 1 - gD.toFloat64())));
    return {
      drift: gEscale.drift, unc: gEscale.getUncertainty(),
      relUnc: gEscale.getRelativeUncertainty(), zone: gEscale.zone, shade: gEscale.shade,
    };
  });

  const derivEl  = document.getElementById('se-derivation');
  const derivRows = [
    ['Framework',   'dT/dt = P_input \u2212 P_scale(T) \u00d7 \u03b7'],
    ['P_scale(T)',  'E_CT \u00d7 (1\u2212(T/T_c)\u2074) \u00d7 k\u1d2eT/\u210f'],
    ['P_input',     `${p_input_w.toExponential(4)} W`],
    ['\u03b7 (coupling)', eta.toFixed(4)],
    ['P_scale peak', `${P_max.toExponential(4)} W at T\u2080 = ${T_peak.toFixed(2)}K (${(T_peak/tc_k*100).toFixed(1)}% T_c)`],
  ];
  const tableRows = [
    ['Material',     material,                    '', `T_c=${tc_k}K  E_CT=${ect_ev}eV`],
    ['P_input',      p_input_w.toExponential(4),  'W', 'External drive power'],
    ['\u03b7 (coupling)', eta.toFixed(4),          '', 'Scale\u2192thermal coupling efficiency'],
    ['P_scale peak', P_max.toExponential(4),       'W', `at T_peak = ${T_peak.toFixed(2)}K`],
  ];

  if (p_input_w > P_max) {
    derivRows.push(['Result', 'No fixed point \u2014 P_input > P_max. Scale emission cannot balance input.', 'deriv-result']);
    tableRows.push(['Fixed Point', 'None', '', `P_input exceeds P_scale max (${P_max.toExponential(3)} W)`]);
    status(`No fixed point \u2014 P_input (${p_input_w.toExponential(3)}) exceeds P_scale max (${P_max.toExponential(3)})`);
  } else {
    if (stableFP != null) {
      const ev_s = -eta * dPdT(stableFP);
      const sf   = Math.max(0, 1 - Math.pow(stableFP / tc_k, 4));
      derivRows.push(['Stable T*',  `${stableFP.toFixed(4)} K (${(stableFP/tc_k*100).toFixed(2)}% T_c)`, 'deriv-result']);
      derivRows.push(['\u03bb stable',    `${ev_s.toExponential(4)} s\u207b\u00b9 \u2014 self-correcting attractor`]);
      tableRows.push(['Stable T*',  stableFP.toFixed(4),  'K',   `${(stableFP/tc_k*100).toFixed(2)}% of T_c`]);
      tableRows.push(['\u03bb (stable)',  ev_s.toExponential(4), 's\u207b\u00b9', '< 0 = stable attractor \u2713']);
      tableRows.push(['Superfluid at T*', sf.toFixed(6), '', 'Cooper pair density']);
    }
    if (unstableFP != null) {
      const ev_u = -eta * dPdT(unstableFP);
      derivRows.push(['Unstable T**', `${unstableFP.toFixed(4)} K \u2014 perturbation diverges past this`]);
      derivRows.push(['\u03bb unstable',  `${ev_u.toExponential(4)} s\u207b\u00b9`]);
      tableRows.push(['Unstable T**', unstableFP.toFixed(4), 'K',   'Push past this \u2192 T drifts to T_c']);
      tableRows.push(['\u03bb (unstable)', ev_u.toExponential(4), 's\u207b\u00b9', '> 0 = unstable \u26a0']);
    }
    derivRows.push(['Interpretation',
      stableFP != null
        ? 'T rises \u2192 P_scale rises \u2192 more cooling \u2192 returns to T*. Geometric negative feedback.'
        : 'No stable attractor at this P_input/\u03b7 combination.']);
    const parts = [];
    if (stableFP   != null) parts.push(`T* = ${stableFP.toFixed(2)}K (stable)`);
    if (unstableFP != null) parts.push(`T** = ${unstableFP.toFixed(2)}K (threshold)`);
    status('Feedback \u2014 ' + parts.join(' | '));
  }

  tableRows.push(['GeoNum drift', geoResult.drift.toFixed(4) + ' shades', '', geoResult.drift < 1.0 ? '\u2713 HPC-grade stable' : '\u26a0 Drifting']);

  let feedbackHtml = geoDerivPanel(
    `Feedback Mode \u2014 Geometric Loop (${material})`, derivRows,
    { drift: geoResult.drift, unc: geoResult.unc, relUnc: geoResult.relUnc, zone: geoResult.zone, shade: geoResult.shade }
  );

  // Casimir prescription — computed at the self-consistent T*, not a hand-picked T
  if (input.lambda_l0_nm && stableFP != null) {
    feedbackHtml += casimirPredictionPanel(tc_k, stableFP, input.lambda_l0_nm, material);
    drawCasimirChart('c-scale-emission', tc_k, stableFP, input.lambda_l0_nm, material);
  } else {
    if (seCharts['c-scale-emission']) {
      seCharts['c-scale-emission'].destroy();
      seCharts['c-scale-emission'] = null;
    }
  }

  if (derivEl) derivEl.innerHTML = feedbackHtml;
  renderTable(['Property', 'Value', 'Unit', 'Notes'], tableRows);
  animatePhasePlot('c-se-bloom',
    { stableFP, unstableFP, T_peak, pScale, eta, p_input_w, P_max },
    tc_k, material);

  const N = 100;
  const casimirMeta = (input.lambda_l0_nm && stableFP != null)
    ? buildCasimirMeta(tc_k, stableFP, input.lambda_l0_nm) : null;
  registerReport({
    title:    `Scale Emission Feedback \u2014 ${material}`,
    module:   'Scale Emission (Feedback)',
    material,
    xLabel:   'T (K)', xMin: 0, xMax: tc_k,
    yLabel:   'P_scale \u00d7 \u03b7 (W)',
    params: [
      ['Material',    material,                        `T_c = ${tc_k} K`],
      ['E_CT',        `${ect_ev} eV`,                  'Condensation energy channel'],
      ['P_input',     `${p_input_w.toExponential(4)} W`, 'External drive power'],
      ['\u03b7',      eta.toFixed(4),                  'Scale\u2192thermal coupling'],
      ['P_scale peak',`${P_max.toExponential(4)} W`,   `at T\u2080 = ${T_peak.toFixed(2)} K`],
      ...(stableFP != null ? [['Stable T*', `${stableFP.toFixed(4)} K`, `${(stableFP/tc_k*100).toFixed(2)}% of T_c`]] : []),
      ...(unstableFP != null ? [['Unstable T**', `${unstableFP.toFixed(4)} K`, 'Perturbation threshold']] : []),
      ...(input.lambda_l0_nm ? [['\u03bb_L(0)', `${input.lambda_l0_nm} nm`, 'London penetration depth']] : []),
    ],
    series: [{
      label: `P_scale(T)\u00d7\u03b7 — GDBS (${material})`,
      color: '#2563eb',
      data:  Array.from({ length: N + 1 }, (_, i) => {
        const T = (i / N) * tc_k * 0.999;
        return [T, pScale(T) * eta];
      }),
    }, {
      label: `P_input = ${p_input_w.toExponential(2)} W`,
      color: '#dc2626',
      dashed: true,
      data:  [[0, p_input_w], [tc_k * 0.999, p_input_w]],
    }],
    annotations: [
      ...(stableFP   != null ? [{ x: stableFP,   label: `T* = ${stableFP.toFixed(2)}K`,   color: '#16a34a' }] : []),
      ...(unstableFP != null ? [{ x: unstableFP, label: `T** = ${unstableFP.toFixed(2)}K`, color: '#dc2626' }] : []),
      { x: T_peak, label: `T\u2080 = ${T_peak.toFixed(2)}K (peak)`, color: '#d97706' },
    ],
    casimir: casimirMeta,
    geoMeta: {
      drift: geoResult.drift, zone: geoResult.zone, shade: geoResult.shade,
      unc:   geoResult.unc,   relUnc: geoResult.relUnc,
    },
  });
}

// ============================================================
// Casimir meta helper
// ============================================================
function buildCasimirMeta(tc_k, t_op, lambda_l0_nm) {
  const sf           = Math.max(0, 1 - Math.pow(t_op / tc_k, 4));
  const lambda_t_nm  = sf > 1e-6 ? lambda_l0_nm / Math.sqrt(sf) : null;
  const d_star_nm    = lambda_t_nm;
  const T_double     = tc_k * Math.pow(0.75, 0.25);
  // Force sensitivity: 1% of Casimir at d = d* (π²ℏc/240d⁴ × 0.01)
  const HBARC        = 3.162e-26; // J·m
  const sensitivity_pa = d_star_nm
    ? (Math.PI ** 2 * HBARC / (240 * Math.pow(d_star_nm * 1e-9, 4))) * 0.01
    : null;
  return { lambda_l0_nm, lambda_t_nm, d_star_nm, T_double, sensitivity_pa };
}

// ============================================================
// Canvas: Bloom Gradient (static mode)
// ============================================================
function animateBloomGradient(canvasId, result, tc_k, t_op, material) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  if (animHandles[canvasId]) cancelAnimationFrame(animHandles[canvasId]);
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const cx = W / 2, cy = H / 2;
  const superfluid = Math.max(0, 1 - Math.pow(t_op / tc_k, 4));
  let frame = 0;

  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, W, H);
    frame++;
    const t = frame * 0.018;
    const nLines = 18, spacing = (H - 40) / nLines;

    for (let i = 0; i < nLines; i++) {
      const y0   = 22 + i * spacing;
      const dist = Math.abs(y0 - cy) / cy;
      const bunch = superfluid * 28 * Math.exp(-dist * 3) * Math.sin(t * 0.7 + i * 0.4);
      const y    = y0 + (y0 < cy ? bunch : -bunch);
      const alpha = 0.15 + 0.25 * (1 - dist) + 0.1 * Math.sin(t + i * 0.6);
      const pull  = superfluid * 60 * Math.exp(-dist * 2);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.bezierCurveTo(W * 0.3, y - pull * 0.4, W * 0.7, y - pull * 0.4, W, y);
      ctx.strokeStyle = `rgba(100,180,255,${alpha})`; ctx.lineWidth = 1; ctx.stroke();
    }

    for (let r = 0; r < 6; r++) {
      const radius = 20 + r * 22 + (frame % 80) * 0.6;
      if (radius > 200) continue;
      const ra = superfluid * 0.35 * (1 - r / 6) * (0.7 + 0.3 * Math.sin(t + r));
      ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(124,58,237,${ra * 0.8})`; ctx.lineWidth = 1.5; ctx.stroke();
    }

    const lp = 0.85 + 0.15 * Math.sin(t * 2.1);
    const lg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 18);
    lg.addColorStop(0,   `rgba(245,158,11,${lp})`);
    lg.addColorStop(0.6, `rgba(124,58,237,${lp * 0.7})`);
    lg.addColorStop(1,   'rgba(124,58,237,0)');
    ctx.beginPath(); ctx.arc(cx, cy, 18, 0, Math.PI * 2); ctx.fillStyle = lg; ctx.fill();
    ctx.beginPath(); ctx.arc(cx, cy, 10, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(245,158,11,${lp})`; ctx.fill();
    ctx.strokeStyle = '#fcd34d'; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.fillStyle = '#fff'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
    ctx.fillText(`${(superfluid * 100).toFixed(0)}%`, cx, cy + 3);

    const gR = 80 + 8 * Math.sin(t * 0.5);
    ctx.beginPath(); ctx.arc(cx, cy, gR, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(16,185,129,0.35)'; ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(16,185,129,0.7)'; ctx.font = '8px monospace'; ctx.textAlign = 'left';
    ctx.fillText('g\u2020 threshold', cx + gR + 4, cy);

    ctx.fillStyle = 'rgba(15,15,30,0.9)'; ctx.fillRect(0, 0, W, 26);
    ctx.fillStyle = 'rgba(160,210,255,0.9)'; ctx.font = '10px monospace'; ctx.textAlign = 'left';
    ctx.fillText(
      `Bloom Gradient \u2014 ${material} | T=${t_op}K / T_c=${tc_k}K | D\u2192${result.d_norm.toExponential(2)}`,
      8, 10
    );
    ctx.fillStyle = 'rgba(100,255,180,0.8)';
    ctx.fillText(
      `E_scale = ${result.e_scale_eV.toFixed(4)} eV | drift = ${result.drift.toFixed(4)} shades`,
      8, 21
    );
    animHandles[canvasId] = requestAnimationFrame(draw);
  }
  draw();
}

// ============================================================
// Canvas: Phase Portrait (feedback mode)
// ============================================================
function animatePhasePlot(canvasId, data, tc_k, material) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  if (animHandles[canvasId]) cancelAnimationFrame(animHandles[canvasId]);
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  let frame = 0;

  const { stableFP, unstableFP, T_peak, pScale, eta, p_input_w, P_max } = data;
  const N = 300;
  const Tvals = Array.from({ length: N }, (_, i) => (i + 0.5) / N * tc_k * 0.9999);
  const Pvals = Tvals.map(T => pScale(T) * eta);

  const padL = 52, padR = 16, padT = 30, padB = 36;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const Pdisp = P_max * 1.25;

  function tx(T) { return padL + (T / tc_k) * plotW; }
  function ty(P) { return padT + plotH - Math.min(1, Math.max(0, P / Pdisp)) * plotH; }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = window.cpal().panel; ctx.fillRect(0, 0, W, H);
    frame++;
    const t = frame * 0.02;

    const xPeak = tx(T_peak);
    ctx.fillStyle = 'rgba(16,185,129,0.07)';
    ctx.fillRect(padL, padT, xPeak - padL, plotH);
    ctx.fillStyle = 'rgba(239,68,68,0.07)';
    ctx.fillRect(xPeak, padT, padL + plotW - xPeak, plotH);

    ctx.beginPath();
    ctx.moveTo(xPeak, padT); ctx.lineTo(xPeak, padT + plotH);
    ctx.strokeStyle = 'rgba(245,158,11,0.4)'; ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]); ctx.stroke(); ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(245,158,11,0.6)'; ctx.font = '8px monospace'; ctx.textAlign = 'center';
    ctx.fillText('T\u2080', xPeak, padT + plotH + 14);
    ctx.fillText('peak', xPeak, padT + plotH + 24);

    ctx.fillStyle = 'rgba(16,185,129,0.45)'; ctx.textAlign = 'left';
    ctx.fillText('stable', padL + 4, padT + plotH - 6);
    ctx.fillStyle = 'rgba(239,68,68,0.45)'; ctx.textAlign = 'right';
    ctx.fillText('unstable', padL + plotW - 2, padT + plotH - 6);

    ctx.beginPath();
    Tvals.forEach((T, i) => {
      const x = tx(T), y = ty(Pvals[i]);
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.strokeStyle = '#4fc3f7'; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = '#4fc3f7'; ctx.font = '8px monospace'; ctx.textAlign = 'left';
    ctx.fillText('P\u209b(T)\u00d7\u03b7', padL + 4, padT + 12);

    const yPin = ty(p_input_w);
    if (yPin >= padT && yPin <= padT + plotH) {
      ctx.beginPath();
      ctx.moveTo(padL, yPin); ctx.lineTo(padL + plotW, yPin);
      ctx.strokeStyle = 'rgba(245,158,11,0.85)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.fillStyle = 'rgba(245,158,11,0.8)'; ctx.textAlign = 'right';
      ctx.fillText(`P_in=${p_input_w.toExponential(2)}W`, padL + plotW - 2, yPin - 4);
    }

    if (stableFP != null) {
      const fpx = tx(stableFP), fpy = ty(p_input_w);
      const pulse = 6 + 3 * Math.sin(t * 2.5);
      ctx.beginPath(); ctx.arc(fpx, fpy, pulse, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(16,185,129,0.22)'; ctx.fill();
      ctx.beginPath(); ctx.arc(fpx, fpy, 4, 0, Math.PI * 2);
      ctx.fillStyle = '#10b981'; ctx.fill();
      ctx.fillStyle = '#10b981'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillText(`T*=${stableFP.toFixed(1)}K`, fpx, fpy - 14);
      ctx.fillText('\u2713 stable', fpx, fpy - 24);
    }

    if (unstableFP != null) {
      const fpx = tx(unstableFP), fpy = ty(p_input_w);
      const pulse = 6 + 3 * Math.sin(t * 2.5 + Math.PI);
      ctx.beginPath(); ctx.arc(fpx, fpy, pulse, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(239,68,68,0.18)'; ctx.fill();
      ctx.beginPath(); ctx.arc(fpx, fpy, 4, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444'; ctx.fill();
      ctx.fillStyle = '#ef4444'; ctx.font = 'bold 8px monospace'; ctx.textAlign = 'center';
      ctx.fillText(`T**=${unstableFP.toFixed(1)}K`, fpx, fpy - 14);
      ctx.fillText('\u26a0 threshold', fpx, fpy - 24);
    }

    ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padL, padT); ctx.lineTo(padL, padT + plotH);
    ctx.lineTo(padL + plotW, padT + plotH);
    ctx.stroke();

    ctx.fillStyle = 'rgba(160,210,255,0.55)'; ctx.font = '8px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('0', padL, padT + plotH + 12);
    ctx.fillText(`${tc_k}K`, padL + plotW, padT + plotH + 12);
    ctx.fillText('T (K)', padL + plotW / 2, H - 4);
    ctx.save();
    ctx.translate(11, padT + plotH / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('P\u209b\u00d7\u03b7 (W)', 0, 0);
    ctx.restore();

    ctx.fillStyle = 'rgba(15,15,30,0.9)'; ctx.fillRect(0, 0, W, 26);
    ctx.fillStyle = 'rgba(160,210,255,0.9)'; ctx.font = '10px monospace'; ctx.textAlign = 'left';
    ctx.fillText(`Phase Portrait \u2014 ${material} | Feedback Loop`, 8, 10);
    const fpSummary = stableFP != null
      ? `T* = ${stableFP.toFixed(2)}K\u2713${unstableFP != null ? `  |  T** = ${unstableFP.toFixed(2)}K threshold` : ''}`
      : 'No fixed point \u2014 reduce P_input below ' + P_max.toExponential(2) + 'W';
    ctx.fillStyle = stableFP != null ? 'rgba(16,185,129,0.9)' : 'rgba(239,68,68,0.9)';
    ctx.fillText(fpSummary, 8, 21);

    animHandles[canvasId] = requestAnimationFrame(draw);
  }
  draw();
}
